/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  filterFEConfigDataCharacteristicStrategy,
  getIsServiceBasedMenuEnabled,
  getIsServiceBasedMenuV2Enabled,
} from "constants/service_based_menu"
import { useEffect, useMemo } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { FEConfigData } from "store/duck/global.type"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { FEEDBACK } from "constants/routes"
import { isInventory } from "./useInventoryContext"

// singleton mutex to prevent duplicate dispatches
// issue: effects may be ran with old values from the store, esp. if they belong in the same render cycle
let isLoading = false

export default function useFEConfig(): {
  data?: FEConfigData
  isLoading: boolean
  disallowedSOCKeys: Set<string>
  showAdditionalPriceDrawerLink?: boolean
  showPurchasability?: boolean
} {
  // mock for now
  const {
    feConfig,
    merchantID,
    outlet,
    outletV2,
    isMenuGroup,
    menuGroupServiceBasedMenuConfig,
    enableTroyNewMerchantEndpoint,
  } = useSelector(
    (s: AppState) => ({
      feConfig: s.global.feConfig,
      merchantID: s.menu.merchantID,
      outlet: s.menu.outlet,
      outletV2: s.menu.outletV2,
      isMenuGroup: s.menu.isMenuGroup,
      menuGroupServiceBasedMenuConfig: s.menu.menuGroupServiceBasedMenuConfig,
      enableTroyNewMerchantEndpoint: s.menu.enableTroyNewMerchantEndpoint,
    }),
    shallowEqual,
  )
  const dispatch = useDispatch()
  const location = useLocation()

  const isFeedbackPage = location.pathname?.startsWith(FEEDBACK)
  const isMenuGroupPage =
    isMenuGroup && !isInventory(location) && !isFeedbackPage // TODO: improve boolean to exclude future pages

  const isEnableServiceBasedMenu = enableTroyNewMerchantEndpoint
    ? getIsServiceBasedMenuV2Enabled(outletV2)
    : getIsServiceBasedMenuEnabled(outlet)
  useEffect(() => {
    // fetch config for store level
    if (
      !isMenuGroupPage &&
      merchantID &&
      feConfig?.forMerchantID !== merchantID &&
      !feConfig?.isLoading &&
      !isLoading &&
      isEnableServiceBasedMenu
    ) {
      isLoading = true
      const resp: any = dispatch(actionCreator.global.loadConfig(merchantID))
      resp.then?.(() => {
        isLoading = false
      })
    }
  }, [
    dispatch,
    feConfig,
    merchantID,
    isEnableServiceBasedMenu,
    isMenuGroupPage,
  ])

  // filter by outlet's supported SOCs
  const { filteredFEConfigData, disallowedSOCKeys } = useMemo(() => {
    const menuGroupFEConfigData = {
      serviceBasedMenuConfig: menuGroupServiceBasedMenuConfig,
    }
    // menu state config for menu group and global state config for outlet
    const feConfigData = isMenuGroupPage
      ? menuGroupFEConfigData
      : feConfig?.data
    const characteristicStrategy = filterFEConfigDataCharacteristicStrategy(
      feConfigData,
      outlet,
      outletV2,
      enableTroyNewMerchantEndpoint,
    )
    return characteristicStrategy
  }, [
    enableTroyNewMerchantEndpoint,
    feConfig,
    outlet,
    outletV2,
    isMenuGroupPage,
    menuGroupServiceBasedMenuConfig,
  ])

  return {
    isLoading:
      !isMenuGroupPage &&
      (feConfig?.isLoading || feConfig?.forMerchantID !== merchantID), // if not equal, just assume it's loading
    data: filteredFEConfigData,
    disallowedSOCKeys,
    showAdditionalPriceDrawerLink: !!filteredFEConfigData
      ?.serviceBasedMenuConfig?.characteristicStrategy?.pricingConfig.length,
    showPurchasability: !!filteredFEConfigData?.serviceBasedMenuConfig
      ?.characteristicStrategy?.purchasableConfig.length,
  }
}
